import type { TFunction } from 'i18next'

import { formatDate, formatMoney, formatNumber, formatPhraseList } from '../../lib/format'
import type { TruckAvailability } from '../availability/api'
import { unitEquipment } from '../availability/unitName'
import type { Load } from '../loads/api'
import type { MatchFactors, MatchReasons } from './api'

/** What a load rides on, read the book's way without DAT's code: "Dry van or Reefer". */
function equipmentReading(load: Load, t: TFunction): string {
  const own = t(`carriers.types.${load.equipment}`)
  return load.alternativeEquipment === null
    ? own
    : t('loads.equipmentOr', { own, alternative: t(`carriers.types.${load.alternativeEquipment}`) })
}

/**
 * Every reason a unit and a load cannot go together, as sentences with both sides' own
 * figures in them — "45,000 lb is more than the 40,000 lb offered" — so the dispatcher
 * never has to open two records to see why. A code this build does not know reads as a
 * translated generic, never as the server's words.
 */
export function exclusionSentences(
  reasons: MatchReasons,
  load: Load,
  posting: TruckAvailability,
  t: TFunction,
  language: string,
): string[] {
  return reasons.exclusions.map((code) => {
    switch (code) {
      case 'Equipment':
        return t('matching.exclusions.Equipment', {
          needs: equipmentReading(load, t),
          unit: unitEquipment(posting, t),
        })
      case 'Features':
        // Every feature the unit lacks, by name: what a dispatcher ticks on the trailer, or
        // asks the carrier about (TRKC-100).
        return t('matching.exclusions.Features', {
          features: formatPhraseList(
            reasons.missingFeatures.map((feature) => t(`equipment.features.${feature}`)),
            language,
          ),
        })
      case 'Securement':
        return t('matching.exclusions.Securement', {
          gear: formatPhraseList(reasons.missingSecurement.map((gear) => t(`availability.gear.${gear}`)), language),
        })
      case 'Length':
        // A straight box truck's box against the length the load states (TRKC-102).
        return t('matching.exclusions.Length', { length: load.lengthFeet, box: posting.boxLengthFeet })
      case 'ReeferRange':
        return t('matching.exclusions.ReeferRange', {
          min: load.reeferMinFahrenheit,
          max: load.reeferMaxFahrenheit,
          unitMin: posting.reeferMinFahrenheit,
          unitMax: posting.reeferMaxFahrenheit,
        })
      case 'Weight':
        return t('matching.exclusions.Weight', {
          weight: formatNumber(load.weightPounds, language),
          room: formatNumber(posting.availableWeightPounds, language),
        })
      case 'Pallets':
        return t('matching.exclusions.Pallets', { pallets: load.palletCount, room: posting.availablePallets })
      case 'FullLoadOnPartialRoom':
        return t('matching.exclusions.FullLoadOnPartialRoom')
      case 'PickupBeforeAvailable':
        return t('matching.exclusions.PickupBeforeAvailable', { date: formatDate(posting.availableFrom, language) })
      case 'PickupAfterAvailable':
        return t('matching.exclusions.PickupAfterAvailable', { date: formatDate(posting.availableUntil, language) })
      case 'BrokerBlocked':
        return t('matching.exclusions.BrokerBlocked', { broker: load.brokerName })
      case 'BrokerInactive':
        return t('matching.exclusions.BrokerInactive', { broker: load.brokerName })
      case 'UnitHeldBack':
        return t('matching.exclusions.UnitHeldBack')
      default:
        return t('matching.exclusions.unknown')
    }
  })
}

/**
 * What a compatible pair still asks the dispatcher to confirm. A letter of DAT's code that
 * Hugo's table does not explain (TRKC-100) is said as a question for the broker; air ride,
 * a lift gate or chains are no longer questions — the load asks for them, and matching
 * judges them.
 */
export function cautionSentences(reasons: MatchReasons, load: Load, t: TFunction): string[] {
  const sentences: string[] = []

  for (const code of reasons.cautions) {
    switch (code) {
      case 'ReeferRangeNotStated':
        sentences.push(
          t('matching.cautions.ReeferRangeNotStated', { min: load.reeferMinFahrenheit, max: load.reeferMaxFahrenheit }),
        )
        break
      case 'EquipmentRequirement':
        sentences.push(
          t('matching.cautions.EquipmentRequirement', {
            code: load.datEquipmentCode ?? reasons.requirementLetters ?? '',
          }),
        )
        break
      case 'Hazmat':
        sentences.push(t('matching.cautions.Hazmat'))
        break
      default:
        sentences.push(t('matching.cautions.unknown'))
    }
  }

  return sentences
}

/**
 * The factor strip: how far, how long the trip, how much per mile, against which minimum,
 * where it ends up and when — every figure the order was decided on, so the screen can say
 * why a match sits where it sits, and the rate per mile can be checked from the miles on
 * the card (GPT-5.6 Sol on #35: the trip is its denominator). The deadhead is said to be a
 * straight line: it is shorter than any road, a ranking signal and never a quote.
 */
export function factorPhrases(
  factors: MatchFactors,
  rateUsd: number | null,
  brokerVerified: boolean,
  t: TFunction,
  language: string,
): string[] {
  const phrases = [
    factors.deadheadMiles === null
      ? t('matching.factors.deadheadUnknown')
      : t('matching.factors.deadhead', { miles: formatNumber(factors.deadheadMiles, language) }),
  ]

  if (factors.tripMiles !== null) {
    phrases.push(t('matching.factors.trip', { miles: formatNumber(factors.tripMiles, language) }))
  }

  if (factors.allInRatePerMile !== null) {
    phrases.push(t('matching.factors.allIn', { rate: formatMoney(factors.allInRatePerMile, 'USD', language) }))
  } else if (factors.ratePerMile !== null) {
    phrases.push(t('matching.factors.overTrip', { rate: formatMoney(factors.ratePerMile, 'USD', language) }))
  } else {
    phrases.push(rateUsd === null ? t('matching.factors.noRate') : t('matching.factors.noTrip'))
  }

  // The posting's minimum is said whenever it has one — compared when there is a rate per
  // mile to set against it, and stated as it is when there is not, so the dispatcher
  // knows what to ask the broker for.
  if (factors.minRatePerMile !== null) {
    const minimum = formatMoney(factors.minRatePerMile, 'USD', language)
    phrases.push(
      factors.belowMinimum === null
        ? t('matching.factors.minimum', { minimum })
        : t(factors.belowMinimum ? 'matching.factors.belowMinimum' : 'matching.factors.aboveMinimum', { minimum }),
    )
  }

  if (factors.toPreferredState) {
    phrases.push(t('matching.factors.preferred'))
  }

  phrases.push(
    factors.daysAfterAvailable === 0
      ? t('matching.factors.pickupSameDay')
      : t('matching.factors.pickupLater', { count: factors.daysAfterAvailable }),
  )

  if (brokerVerified) {
    phrases.push(t('matching.factors.verified'))
  }

  return phrases
}
